import React, { useEffect, useState, useMemo } from 'react';
import { Card, Row, Col, Typography } from 'antd';
import { ComparisonRowTitleComponent, colorTableRow } from "./common";
import { stringMixin } from "../../utils/strings";
import DraggableTable from '../../components/DraggableTable';
import PnL from "../../components/PnL";
import {darkenHexColor} from "../../utils/colorUtils";

const TIME_FRAMES = {
  short: ['1_mo', '3_mo', '6_mo'],
  long: ['1_yr', '3_yr', '5_yr']
};

const PerformanceMetrics = ({ tickerData, performanceType, colors }) => {
  const [processedData, setProcessedData] = useState([]);
  const [volData, setVolData] = useState([]);
  const [rocData, setRocData] = useState([]);
  const [chartColors, setChartColors] = useState({});

  console.log('tickerData', tickerData);

  useEffect(() => {
    // Ensure data is structured and valid before processing
    const processed = tickerData.map(ticker => ({
      key: ticker.symbol,
      ...ticker,
    })).filter(ticker => ticker.stats && ticker.stats.windows);

    setProcessedData(processed);

    const groupedData = processData(processed, performanceType);
    setVolData(groupedData.vol);
    setRocData(groupedData.roc);
  }, [tickerData, performanceType]);

  const processData = (data, type) => {
    let volData = [];
    let rocData = [];
    const tempColors = {};

    TIME_FRAMES[type].forEach(timeFrame => {
      let volGroup = [];
      let rocGroup = [];

      data.forEach(ticker => {
        if (ticker.stats && ticker.stats.windows[timeFrame]) {
          volGroup.push({
            id: `${ticker.symbol}-${timeFrame}`,
            value: ticker.stats.windows[timeFrame].hv,
            group: timeFrame,
            size: 3
          });
          rocGroup.push({
            id: `${ticker.symbol}-${timeFrame}`,
            value: ticker.stats.windows[timeFrame].roc,
            group: timeFrame,
            size: 2
          });
        }
        tempColors[`${ticker.symbol}-${timeFrame}`] = colors[ticker.symbol];
      });
      setChartColors(tempColors);
      volGroup.sort((a, b) => b.value - a.value);
      rocGroup.sort((a, b) => b.value - a.value);
      const n = volGroup.length;
      for (let i = 0; i < volGroup.length; i++) {
        volGroup[i].size = n - i > 0 ? n - i : 1;
      }
      for (let i = 0; i < rocGroup.length; i++) {
        rocGroup[i].size = n - i > 0 ? n - i : 1;
      }
      volData = volData.concat(volGroup);
      rocData = rocData.concat(rocGroup);
    });

    return { vol: volData, roc: rocData };
  };

  const generateColumns = (metrics) => [
      {
        title: 'Symbol',
        dataIndex: 'symbol',
        key: 'symbol',
        render: (_, record) => <ComparisonRowTitleComponent symbol={record.symbol} record={record} />,
        width: 250
      },
    ...metrics.flatMap(metric => [
      {
        title: `${metric} Return`,
        dataIndex: ['stats', 'windows', metric, 'roc'],
        key: `${metric}_roc`,
        render: value => <PnL value={value} />,
        sorter: (a, b) => (b.stats?.windows[metric]?.roc ?? 0) - (a.stats?.windows[metric]?.roc ?? 0),
      },
      {
        title: `${metric} Vol`,
        dataIndex: ['stats', 'windows', metric, 'hv'],
        key: `${metric}_hv`,
        render: value => stringMixin.pct(value),
        sorter: (a, b) => (b.stats?.windows[metric]?.hv ?? 0) - (a.stats?.windows[metric]?.hv ?? 0),
      }
    ])
  ];

  const columns = useMemo(() => generateColumns(TIME_FRAMES[performanceType]), [performanceType]);

  return (
      <Card bordered={false}
            title={`Performance Metrics (${performanceType === 'short' ? 'Short' : 'Long'} Term)`}
            style={{maxWidth: '95%'}}
      >
        <DraggableTable
          columns={columns}
          data={processedData}
          setData={setProcessedData}
          tableProps={{ onRow: colorTableRow, pagination: false, scroll: {y: 800} }}
        />
      </Card>
  );
};

export default PerformanceMetrics;
